import { buildNarrativeEcosystem } from './buildNarrativeEcosystem';
import { UMBRALES_IVT } from './umbralesVITA';

export function buildTerritorialOverview({
  actors = [],
  alerts = [],
  signals = [],
  timeline = [],
  filters = {}
}) {
  const filteredActors = filterByTerritory(actors, filters);
  const filteredAlerts = filterByTerritory(alerts, filters);
  const filteredSignals = filterByTerritory(signals, filters);
  const filteredTimeline = filterByTerritory(timeline, filters);

  const totalMovements =
    filteredActors.length +
    filteredAlerts.length +
    filteredSignals.length +
    filteredTimeline.length;

  const narrativeEcosystem = buildNarrativeEcosystem({
    actors: filteredActors,
    alerts: filteredAlerts,
    signals: filteredSignals,
    timeline: filteredTimeline
  });

  const dominantNarrative = narrativeEcosystem.dominante;

  // ─────────────────────────────────────────────
  // Usar datos cognitivos del engine si existen
  // ─────────────────────────────────────────────
  const actoresConVita = filteredActors.filter((a) => a._vita);

  const sensitiveActors = filteredActors.filter((actor) => {
    // Primero usar estado calculado por engine
    if (actor._vita?.state?.state) {
      return ['Activación Territorial', 'Convergencia Narrativa', 'Escalamiento Territorial', 'Sensibilidad Creciente']
        .includes(actor._vita.state.state);
    }
    // Fallback a datos crudos
    return (
      ['Alto', 'Crítico', 'Alta', 'Crítica'].includes(actor.riesgo) ||
      ['Alto', 'Crítico', 'Alta', 'Crítica'].includes(actor.sensibilidadTerritorial) ||
      ['Alta', 'Crítica'].includes(actor.influencia)
    );
  });

  const criticalAlerts = filteredAlerts.filter((alert) =>
    ['Alta', 'Crítica', 'Alto', 'Crítico'].includes(alert.nivel) ||
    ['Alta', 'Crítica', 'Alto', 'Crítico'].includes(alert.riesgo)
  );

  const negativeSignals = filteredSignals.filter((signal) =>
    signal.sentimiento === 'Negativo' ||
    ['Alta', 'Crítica', 'Alto', 'Crítico'].includes(signal.riesgo)
  );

  // Score: usar IVT promedio del engine si disponible
  const score = actoresConVita.length > 0
    ? calculateScoreFromEngine(actoresConVita, criticalAlerts, negativeSignals, filteredTimeline)
    : calculateOverviewScore({ sensitiveActors, criticalAlerts, negativeSignals, filteredTimeline });

  // Estado dominante: usar el peor estado del engine si disponible
  const state = actoresConVita.length > 0
    ? buildOverviewStateFromEngine(actoresConVita, score, { criticalAlerts, negativeSignals })
    : buildOverviewState(score, { criticalAlerts, negativeSignals });

  const confianza = calculateConfianza({
    actors: filteredActors,
    alerts: filteredAlerts,
    signals: filteredSignals,
    snapshots: []
  });

  const dominantActors = getDominantActors(filteredActors);

  const eventosCriticos = getCriticalEvents({
    alerts: criticalAlerts,
    signals: negativeSignals,
    timeline: filteredTimeline
  });

  return {
    scopeLabel: buildScopeLabel(filters),
    estadoTerritorial: state.estado,
    severidad: state.severidad,
    descripcionEstado: state.descripcion,
    confianza,
    narrativaDominante: dominantNarrative,
    ecosistemaNarrativo: narrativeEcosystem,
    riesgoGeneral: score,
    propagacion: buildPropagationLevel(score, dominantActors.length, negativeSignals.length),
    tendencia: buildTrend(score, criticalAlerts.length, negativeSignals.length),
    actoresDominantes: dominantActors,
    eventosCriticos,
    totalMovements,
    accionSugerida: buildSuggestedAction(score),
    filteredActors,
    filteredAlerts,
    filteredSignals,
    filteredTimeline
  };
}

// ─────────────────────────────────────────────
// CONFIANZA COGNITIVA
// Mide cuánta evidencia REAL sostiene la interpretación.
// No depende de dropdowns: depende de señales, alertas,
// perfiles enriquecidos e historial temporal (snapshots).
// Master Core: nunca fingir omnisciencia.
// ─────────────────────────────────────────────

function calculateConfianza({ actors, alerts, signals, snapshots }) {
  // Actores con perfil "rico": tienen narrativas o campos cognitivos
  // declarados por el analista (no solo los 3 dropdowns básicos).
  const actoresRicos = actors.filter((a) => {
    const tieneNarrativas =
      (Array.isArray(a.narrativas) && a.narrativas.length > 0) ||
      (a.narrativaDominante && String(a.narrativaDominante).trim());
    const tieneCognitivos =
      a.sensibilidadTerritorial ||
      a.legitimidadTerritorial ||
      a.capacidadPropagacion ||
      a.capacidadMovilizacion ||
      a.exposicionTerritorial;
    return tieneNarrativas || tieneCognitivos;
  }).length;

  const totalActores = actors.length;

  // Puntaje de evidencia (cada fuente real suma)
  let evidencia = 0;
  evidencia += signals.length * 2;        // señales: materia prima fuerte
  evidencia += alerts.length * 2;         // alertas: evidencia registrada
  evidencia += actoresRicos * 3;          // perfiles enriquecidos pesan más
  evidencia += (snapshots?.length || 0);  // historial temporal

  // Proporción de actores con perfil rico (penaliza si casi todos son dropdowns)
  const proporcionRicos = totalActores > 0 ? actoresRicos / totalActores : 0;

  // Sin actores no hay nada que interpretar
  if (totalActores === 0) {
    return {
      nivel: 'Sin datos',
      score: 0,
      detalle: 'No hay stakeholders registrados.'
    };
  }

  let nivel = 'Baja';
  if (evidencia >= 18 && proporcionRicos >= 0.5) {
    nivel = 'Alta';
  } else if (evidencia >= 8 && proporcionRicos >= 0.25) {
    nivel = 'Media';
  }

  const detalle = buildConfianzaDetalle({
    nivel,
    signals: signals.length,
    alerts: alerts.length,
    actoresRicos,
    totalActores,
    snapshots: snapshots?.length || 0
  });

  return { nivel, score: evidencia, detalle };
}

function buildConfianzaDetalle({ nivel, signals, alerts, actoresRicos, totalActores, snapshots }) {
  if (nivel === 'Alta') {
    return `Interpretación sostenida por evidencia sólida: ${signals} señal(es), ${alerts} alerta(s) y ${actoresRicos} de ${totalActores} actores con perfil enriquecido.`;
  }
  if (nivel === 'Media') {
    return `Evidencia parcial: ${signals} señal(es), ${alerts} alerta(s), ${actoresRicos} de ${totalActores} actores con perfil enriquecido. Enriquecer perfiles y registrar señales aumentaría la confianza.`;
  }
  return `Evidencia insuficiente: ${actoresRicos} de ${totalActores} actores con perfil enriquecido, ${signals} señal(es), ${alerts} alerta(s). La interpretación es preliminar — se basa principalmente en datos declarados, no en señales territoriales observadas.`;
}

// ─────────────────────────────────────────────
// SCORE DESDE ENGINE COGNITIVO
// Usa IVT real de cada actor procesado
// ─────────────────────────────────────────────

function calculateScoreFromEngine(actoresConVita, criticalAlerts, negativeSignals, timeline) {
  const ivtPromedio = actoresConVita.reduce(
    (sum, a) => sum + (a._vita?.scoring?.IVT || 0), 0
  ) / actoresConVita.length;

  let score = ivtPromedio * 0.6;
  score += criticalAlerts.length * 8;
  score += negativeSignals.length * 6;
  score += timeline.length * 2;

  return Math.min(Math.round(score), 100);
}

// ─────────────────────────────────────────────
// ESTADO DESDE ENGINE COGNITIVO
// Usa el estado más severo calculado por el engine
// ─────────────────────────────────────────────

const ESTADO_SEVERIDAD = {
  'Escalamiento Territorial': 6,
  'Convergencia Narrativa': 5,
  'Activación Territorial': 4,
  'Sensibilidad Creciente': 3,
  'Observación': 2,
  'Contención Territorial': 1,
  'Estabilización': 1,
  'Estable': 0
};

function buildOverviewStateFromEngine(actoresConVita, score, { criticalAlerts, negativeSignals }) {
  let estadoDominante = 'Estable';
  let maxSeveridad = -1;

  for (const actor of actoresConVita) {
    const estado = actor._vita?.state?.state || 'Estable';
    const sev = ESTADO_SEVERIDAD[estado] ?? 0;
    if (sev > maxSeveridad) {
      maxSeveridad = sev;
      estadoDominante = estado;
    }
  }

  const descripciones = {
    'Escalamiento Territorial': 'El territorio presenta alta presión acumulada con riesgo de escalamiento relevante.',
    'Convergencia Narrativa': 'Se detecta alineación narrativa entre actores con potencial de movilización.',
    'Activación Territorial': 'Se observan señales activas, actores sensibles y presión territorial creciente.',
    'Sensibilidad Creciente': 'El territorio presenta sensibilidad acumulada que requiere gestión preventiva.',
    'Observación': 'Existen señales iniciales que deben monitorearse para evitar escalamiento.',
    'Contención Territorial': 'El territorio está en proceso de contención activa.',
    'Estabilización': 'El territorio muestra señales de estabilización progresiva.',
    'Estable': 'No se observan señales territoriales relevantes actualmente.'
  };

  const severidades = {
    'Escalamiento Territorial': 'Crítica',
    'Convergencia Narrativa': 'Alta',
    'Activación Territorial': 'Alta',
    'Sensibilidad Creciente': 'Media-Alta',
    'Observación': 'Media',
    'Contención Territorial': 'Media',
    'Estabilización': 'Baja',
    'Estable': 'Baja'
  };

  return {
    estado: estadoDominante,
    severidad: severidades[estadoDominante] || 'Baja',
    descripcion: descripciones[estadoDominante] || 'Estado territorial en análisis.'
  };
}

// ─────────────────────────────────────────────
// SCORE FALLBACK (sin engine)
// ─────────────────────────────────────────────

function calculateOverviewScore({ sensitiveActors, criticalAlerts, negativeSignals, filteredTimeline }) {
  let score = 0;
  score += sensitiveActors.length * 12;
  score += criticalAlerts.length * 18;
  score += negativeSignals.length * 14;
  score += filteredTimeline.length * 4;
  return Math.min(score, 100);
}

// FALLBACK (sin engine). Hoy prácticamente no se ejecuta porque los
// actores llegan con _vita. Se mantiene alineado a la tabla única
// por seguridad. Candidato a eliminación en limpieza futura.
function buildOverviewState(score, { criticalAlerts, negativeSignals }) {
  if (score >= UMBRALES_IVT.ESCALAMIENTO) return {
    estado: 'Escalamiento Territorial',
    severidad: 'Crítica',
    descripcion: 'El territorio presenta alta presión acumulada y riesgo de escalamiento.'
  };
  if (score >= UMBRALES_IVT.ACTIVACION) return {
    estado: 'Activación Territorial',
    severidad: 'Media-Alta',
    descripcion: 'Se observan señales activas, actores sensibles y presión territorial creciente.'
  };
  if (score >= UMBRALES_IVT.SENSIBILIDAD) return {
    estado: 'Sensibilidad Creciente',
    severidad: 'Media',
    descripcion: 'El territorio presenta sensibilidad acumulada que requiere gestión preventiva.'
  };
  if (criticalAlerts.length > 0 || negativeSignals.length > 0 || score >= UMBRALES_IVT.OBSERVACION) return {
    estado: 'Observación',
    severidad: 'Baja',
    descripcion: 'Existen señales iniciales que deben monitorearse.'
  };
  return {
    estado: 'Estable',
    severidad: 'Baja',
    descripcion: 'No se observan señales territoriales relevantes.'
  };
}

// ─────────────────────────────────────────────
// ACTORES DOMINANTES
// Ordenados por IVT del engine si disponible
// ─────────────────────────────────────────────

function getDominantActors(actors) {
  return actors
    .filter((actor) => {
      if (actor._vita?.scoring?.IVT !== undefined) {
        return actor._vita.scoring.IVT >= 40;
      }
      return (
        actor.influencia === 'Alta' ||
        actor.sensibilidadTerritorial === 'Alta' ||
        actor.riesgo === 'Alto' ||
        actor.riesgo === 'Crítico'
      );
    })
    .sort((a, b) => (b._vita?.scoring?.IVT || 0) - (a._vita?.scoring?.IVT || 0))
    .slice(0, 5)
    .map((actor) => actor.nombre || 'Actor territorial');
}

// ─────────────────────────────────────────────
// RESTO DE UTILIDADES
// ─────────────────────────────────────────────

function filterByTerritory(items, filters) {
  return items.filter((item) => {
    const regionOk = !filters.region || filters.region === 'Todas las regiones' || item.region === filters.region;
    const comunaOk = !filters.comuna || filters.comuna === 'Todas las comunas' || item.comuna === filters.comuna;
    const sectorOk = !filters.sector || filters.sector === 'Todos los sectores' || item.sector === filters.sector;
    const focoOk = !filters.foco || filters.foco === 'Todos los focos' || item.foco === filters.foco;
    return regionOk && comunaOk && sectorOk && focoOk;
  });
}

function getCriticalEvents({ alerts, signals, timeline }) {
  return [
    ...alerts.map((item) => item.titulo || 'Alerta territorial'),
    ...signals.map((item) => item.texto || item.descripcion || 'Señal RRSS negativa'),
    ...timeline.map((item) => item.titulo || 'Evento territorial')
  ].slice(0, 5);
}

function buildPropagationLevel(score, actorsCount, signalsCount) {
  if (score >= 70 || (actorsCount >= 4 && signalsCount >= 2)) return 'Alta';
  if (score >= 40 || actorsCount >= 2) return 'Media';
  if (score >= 20) return 'Baja';
  return 'Sin propagación relevante';
}

function buildTrend(score, alertsCount, signalsCount) {
  if (score >= 70 || alertsCount >= 2 || signalsCount >= 3) return 'Escalando';
  if (score >= 40) return 'En observación';
  if (score >= 20) return 'Latente';
  return 'Estable';
}

function buildSuggestedAction(score) {
  if (score >= 70) return 'Activar gestión preventiva inmediata con actores clave y monitoreo reforzado durante las próximas 48 horas.';
  if (score >= 40) return 'Realizar seguimiento territorial dirigido y preparar estrategia preventiva de comunicación.';
  if (score >= 20) return 'Mantener observación activa y registrar nuevas señales territoriales.';
  return 'Mantener monitoreo regular sin activar gestión extraordinaria.';
}

function buildScopeLabel(filters) {
  const parts = [filters.region, filters.comuna, filters.sector, filters.foco]
    .filter((item) => item && !String(item).startsWith('Todas') && !String(item).startsWith('Todos'));
  return parts.length > 0
    ? `Contexto territorial activo · ${parts.join(' · ')}`
    : 'Visión territorial general';
}